(() => {
  const shell = document.querySelector('[data-cart-drawer-shell]');
  const drawer = document.querySelector('[data-cart-drawer]');

  if (!shell || !drawer) return;

  const itemsContainer = drawer.querySelector('[data-cart-drawer-items]');
  const emptyState = drawer.querySelector('[data-cart-drawer-empty]');
  const footer = drawer.querySelector('[data-cart-drawer-footer]');
  const subtotal = drawer.querySelector('[data-cart-subtotal]');
  const savingsRow = drawer.querySelector('[data-cart-savings-row]');
  const savingsTotal = drawer.querySelector('[data-cart-total-savings]');
  const drawerCount = drawer.querySelector('[data-cart-drawer-count]');
  const status = drawer.querySelector('[data-cart-drawer-status]');
  const shippingMessage = drawer.querySelector('[data-cart-free-shipping-message]');
  const shippingProgress = drawer.querySelector('[data-cart-free-shipping-progress]');
  const toastRegion = document.querySelector('[data-cart-toast-region]');
  const freeShippingThreshold = Number(drawer.dataset.freeShippingThreshold || 0);
  const currency = drawer.dataset.currency || 'USD';
  const cartUrl = drawer.dataset.cartUrl || '/cart.js';
  const addUrl = drawer.dataset.cartAddUrl || '/cart/add.js';
  const changeUrl = drawer.dataset.cartChangeUrl || '/cart/change.js';
  const updateUrl = drawer.dataset.cartUpdateUrl || '/cart/update.js';
  const cartPageUrl = drawer.dataset.cartPageUrl || '/cart';
  const redoProductIds = new Set(['7818317365428']);
  const redoProductHandle = 'free-unlimited-returns-for-store-credit-or-exchanges-package-protection-2';
  const redoSkuPattern = /^x-redo/i;
  const redoVendorPattern = /^redo$/i;
  const redoTitlePattern = /(package protection|free refunds|free returns|unlimited returns)/i;
  const redoExchangeStorageKey = 'redo.exchange-select';
  const iconTemplates = {};
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let lastFocusedElement = null;
  let isOpen = false;
  let toastTimer = null;
  let toastHideTimer = null;
  let toastRequestId = 0;
  const toastDuration = 5000;
  const originalUnitPricesByKey = new Map();
  const originalUnitPricesByVariant = new Map();
  const productJsonByHandle = new Map();
  const hydratedVariantIds = new Set();

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  drawer.querySelectorAll('[data-cart-icon-template]').forEach((template) => {
    iconTemplates[template.dataset.cartIconTemplate] = template.innerHTML.trim();
  });

  const getIcon = (name) => iconTemplates[name] || '';

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const normalizeImageUrl = (url, width = 180) => {
    if (!url) return '';

    const absoluteUrl = String(url).startsWith('//') ? `https:${url}` : String(url);
    const separator = absoluteUrl.includes('?') ? '&' : '?';

    return `${absoluteUrl}${separator}width=${width}`;
  };

  const getCartError = async (response, fallbackMessage) => {
    const error = await response.json().catch(() => ({}));

    return new Error(error.description || error.message || fallbackMessage);
  };

  const postCartJson = async (url, body, fallbackMessage) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) throw await getCartError(response, fallbackMessage);
    return response.json();
  };

  const getLineNumber = (value) => {
    const number = Number.parseInt(value, 10);

    return Number.isFinite(number) && number > 0 ? number : null;
  };

  const cartHasKey = (cart, key) => {
    return Boolean(key && cart?.items?.some((item) => item.key === key));
  };

  const toStringId = (value) => {
    if (value === null || value === undefined) return '';

    const match = String(value).match(/(\d+)$/);
    return match ? match[1] : String(value);
  };

  const getItemVariantId = (item) => {
    return toStringId(item?.variant_id || item?.id);
  };

  const isRedoExchangeSession = () => {
    try {
      return window.sessionStorage.getItem(redoExchangeStorageKey) !== null;
    } catch (error) {
      return false;
    }
  };

  const cacheOriginalUnitPrice = ({ key = '', variantId = '', unitPrice = 0 } = {}) => {
    const normalizedUnitPrice = Number(unitPrice || 0);

    if (!Number.isFinite(normalizedUnitPrice) || normalizedUnitPrice <= 0) return;
    if (key) originalUnitPricesByKey.set(String(key), normalizedUnitPrice);
    if (variantId) originalUnitPricesByVariant.set(String(variantId), normalizedUnitPrice);
  };

  const seedOriginalUnitPriceCache = () => {
    drawer.querySelectorAll('[data-cart-drawer-item][data-cart-original-unit-price]').forEach((element) => {
      cacheOriginalUnitPrice({
        key: element.dataset.lineKey,
        variantId: element.dataset.variantId,
        unitPrice: element.dataset.cartOriginalUnitPrice
      });
    });
  };

  const getCachedOriginalUnitPrice = (item) => {
    const key = item?.key ? String(item.key) : '';
    const variantId = getItemVariantId(item);

    return Number(
      (key && originalUnitPricesByKey.get(key))
      || (variantId && originalUnitPricesByVariant.get(variantId))
      || 0
    );
  };

  seedOriginalUnitPriceCache();

  const isRedoLineItem = (item) => {
    if (!item) return false;

    const productId = toStringId(item.product_id);
    const sku = String(item.sku || '');
    const vendor = String(item.vendor || '');
    const url = String(item.url || item.handle || '');
    const title = String(item.product_title || item.title || '');

    return redoProductIds.has(productId)
      || redoSkuPattern.test(sku)
      || redoVendorPattern.test(vendor)
      || url.includes(redoProductHandle)
      || (redoTitlePattern.test(title) && redoSkuPattern.test(sku));
  };

  const getVisibleCartEntries = (cart) => {
    return Array.isArray(cart?.items)
      ? cart.items
        .map((item, index) => ({ item, index }))
        .filter((entry) => !isRedoLineItem(entry.item))
      : [];
  };

  const getVisibleCartTotal = (entries) => {
    return entries.reduce((sum, entry) => sum + Number(entry.item.final_line_price || 0), 0);
  };

  const getVisibleCartOriginalTotal = (entries) => {
    return entries.reduce((sum, entry) => {
      const originalLinePrice = getItemOriginalLinePrice(entry.item);

      return sum + Number(originalLinePrice || 0);
    }, 0);
  };

  const getVisibleCartExchangeTotal = (entries) => {
    return entries.reduce((sum, entry) => {
      return sum + getItemExchangeLinePrice(entry.item);
    }, 0);
  };

  const getItemCompareAtPrice = (item) => {
    return Number(
      item?.compare_at_price
      || item?.variant_compare_at_price
      || item?.variant?.compare_at_price
      || 0
    );
  };

  const getItemExchangeLinePrice = (item) => {
    const quantity = Math.max(Number(item?.quantity || 0), 1);
    const finalLinePrice = Number(item?.final_line_price || 0);
    const unitPrice = Number(item?.original_price || item?.price || item?.final_price || 0);

    return Number(item?.original_line_price || item?.line_price || (unitPrice * quantity) || finalLinePrice);
  };

  const getItemOriginalLinePrice = (item) => {
    const quantity = Math.max(Number(item?.quantity || 0), 1);
    const finalLinePrice = Number(item?.final_line_price || 0);
    const originalLinePrice = Number(item?.original_line_price || item?.line_price || finalLinePrice);
    const compareAtPrice = getItemCompareAtPrice(item);
    const compareAtLinePrice = compareAtPrice > 0 ? compareAtPrice * quantity : 0;
    const cachedOriginalLinePrice = getCachedOriginalUnitPrice(item) * quantity;

    return Math.max(finalLinePrice, originalLinePrice, compareAtLinePrice, cachedOriginalLinePrice);
  };

  const getItemProductHandle = (item) => {
    if (item?.handle) return String(item.handle);

    const match = String(item?.url || '').match(/\/products\/([^/?#]+)/i);
    return match?.[1] || '';
  };

  const requestProductJson = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!productJsonByHandle.has(handle)) {
      productJsonByHandle.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' }
        })
          .then((response) => response.ok ? response.json() : null)
          .catch(() => null)
      );
    }

    return productJsonByHandle.get(handle);
  };

  const hydrateOriginalUnitPrices = async (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const pendingItems = items.filter((item) => {
      const variantId = getItemVariantId(item);
      return variantId && !hydratedVariantIds.has(variantId) && getCachedOriginalUnitPrice(item) <= 0;
    });

    if (!pendingItems.length) return;

    await Promise.all(pendingItems.map(async (item) => {
      const variantId = getItemVariantId(item);
      const product = await requestProductJson(getItemProductHandle(item));
      const variant = Array.isArray(product?.variants)
        ? product.variants.find((entry) => toStringId(entry?.id) === variantId)
        : null;
      const originalUnitPrice = Math.max(
        Number(variant?.compare_at_price || 0),
        Number(variant?.price || 0),
        Number(item?.original_price || item?.price || item?.final_price || 0)
      );

      cacheOriginalUnitPrice({
        key: item.key,
        variantId,
        unitPrice: originalUnitPrice
      });
      hydratedVariantIds.add(variantId);
    }));
  };

  const getVisibleCartCount = (entries) => {
    return entries.reduce((sum, entry) => sum + Number(entry.item.quantity || 0), 0);
  };

  const hideToast = ({ cancelPending = true } = {}) => {
    if (!toastRegion) return;

    if (cancelPending) toastRequestId += 1;
    window.clearTimeout(toastTimer);
    window.clearTimeout(toastHideTimer);
    toastRegion.classList.remove('cart-toast-region--active');

    toastHideTimer = window.setTimeout(() => {
      toastRegion.hidden = true;
      toastRegion.innerHTML = '';
    }, prefersReducedMotion ? 0 : 220);
  };

  const getToastMedia = (item) => {
    const image = item?.featured_image || {};
    const imageUrl = normalizeImageUrl(image.url || item?.image, 120);
    const imageAlt = escapeHtml(image.alt || item?.product_title || item?.title || 'Product image');
    const imageRatio = Number(image.aspect_ratio || 0);
    const imageHeight = imageRatio > 0 ? Math.round(120 / imageRatio) : 160;

    if (!imageUrl) return null;

    return {
      alt: imageAlt,
      height: imageHeight,
      href: item.url || cartPageUrl,
      url: imageUrl
    };
  };

  const preloadImage = (url) => {
    if (!url) return Promise.resolve(false);

    return new Promise((resolve) => {
      const image = new Image();
      let finished = false;

      const finish = (loaded) => {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeout);
        resolve(loaded);
      };

      const decode = () => {
        if (typeof image.decode === 'function') {
          image.decode().then(() => finish(true)).catch(() => finish(true));
          return;
        }

        finish(true);
      };

      const timeout = window.setTimeout(() => finish(false), 4000);

      image.onload = decode;
      image.onerror = () => finish(false);
      image.src = url;

      if (image.complete && image.naturalWidth > 0) decode();
    });
  };

  const getLoadedToastMedia = async (media) => {
    if (!media?.url) return null;

    const didLoad = await preloadImage(media.url);

    return didLoad ? media : null;
  };

  const renderToastMedia = (media) => {
    if (!media?.url) return '';

    return `
      <a class="cart-toast__media" href="${escapeHtml(media.href || cartPageUrl)}" tabindex="-1">
        <img
          src="${escapeHtml(media.url)}"
          width="120"
          height="${media.height}"
          alt="${media.alt}"
          loading="eager"
          decoding="async"
        >
      </a>
    `;
  };

  const formatOptionValue = (value) => {
    return String(value || '')
      .replace(/[-_]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getNamedOptionValue = (item, names) => {
    const normalizedNames = names.map((name) => name.toLowerCase());
    const options = Array.isArray(item?.options_with_values) ? item.options_with_values : [];
    const option = options.find((candidate) => {
      const candidateName = String(candidate.name || '').toLowerCase();

      return normalizedNames.some((name) => candidateName.includes(name));
    });

    return option?.value || '';
  };

  const renderToastDetails = (item) => {
    if (!item) return '';

    const details = [];
    const color = formatOptionValue(getNamedOptionValue(item, ['color', 'colour']));
    const size = formatOptionValue(getNamedOptionValue(item, ['size']));

    if (color) details.push(color);
    if (size) details.push(size);

    if (!details.length && item.variant_title && item.variant_title !== 'Default Title') {
      details.push(formatOptionValue(item.variant_title));
    }

    if (!details.length && Array.isArray(item.variant_options)) {
      const variantOptions = item.variant_options
        .filter((option) => option && option !== 'Default Title')
        .map(formatOptionValue);

      details.push(...variantOptions);
    }

    if (!details.length) return '';

    return `<p class="cart-toast__details">${escapeHtml(details.join(' / '))}</p>`;
  };

  const showToast = async ({
    heading = 'Added to bag',
    message = '',
    type = 'success',
    item = null,
    undo = null
  } = {}) => {
    if (!toastRegion) return;

    const requestId = toastRequestId + 1;
    toastRequestId = requestId;
    window.clearTimeout(toastTimer);
    window.clearTimeout(toastHideTimer);
    toastRegion.classList.remove('cart-toast-region--active');
    toastRegion.hidden = true;
    toastRegion.innerHTML = '';

    const undoMarkup = undo?.key
      ? `
        <button
          class="cart-toast__action cart-toast__action--secondary"
          type="button"
          aria-label="Undo add"
          data-cart-toast-undo
          data-cart-toast-undo-key="${escapeHtml(undo.key)}"
          data-cart-toast-undo-line="${undo.line || ''}"
          data-cart-toast-undo-quantity="${undo.quantity || 1}"
        >
          ${getIcon('undo')}
        </button>
      `
      : '';

    const viewBagMarkup = type === 'success'
      ? `
        <button class="cart-toast__action cart-toast__action--view-bag" type="button" aria-label="Open bag" data-cart-toast-open-bag>
          ${getIcon('bag')}
        </button>
      `
      : '';

    const media = await getLoadedToastMedia(getToastMedia(item));
    if (requestId !== toastRequestId) return;

    const mediaMarkup = renderToastMedia(media);

    toastRegion.hidden = false;
    toastRegion.innerHTML = `
      <div class="cart-toast cart-toast--${escapeHtml(type)}${mediaMarkup ? '' : ' cart-toast--no-media'}" style="--cart-toast-duration: ${toastDuration}ms;">
        <span class="cart-toast__progress" aria-hidden="true"></span>
        ${mediaMarkup}
        <div class="cart-toast__content">
          <p class="cart-toast__heading">${escapeHtml(heading)}</p>
          ${message ? `<p class="cart-toast__message">${escapeHtml(message)}</p>` : ''}
          ${renderToastDetails(item)}
        </div>
        <div class="cart-toast__controls">
          ${viewBagMarkup}
          ${undoMarkup}
          <button class="cart-toast__close" type="button" aria-label="Close notification" data-cart-toast-close>
            ${getIcon('close')}
          </button>
        </div>
      </div>
    `;

    window.requestAnimationFrame(() => {
      if (requestId !== toastRequestId) return;
      toastRegion.classList.add('cart-toast-region--active');
    });

    toastTimer = window.setTimeout(() => hideToast({ cancelPending: false }), toastDuration);
  };

  const setStatus = (message) => {
    if (!status) return;

    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message || '';
    });
  };

  const getFocusableElements = () => {
    return Array.from(drawer.querySelectorAll(focusableSelector)).filter((element) => {
      return element.offsetParent !== null || element === document.activeElement;
    });
  };

  const updateHeaderCounts = (count) => {
    const label = count === 1 ? '1 item in bag' : `${count} items in bag`;

    if (drawerCount) drawerCount.textContent = `(${count})`;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count <= 0;
    });

    document.querySelectorAll('[data-cart-count-label]').forEach((element) => {
      element.setAttribute('aria-label', label);
    });
  };

  const updateShippingMessage = ({ count, total }) => {
    if (!shippingMessage || !shippingProgress) return;

    const progressMax = freeShippingThreshold > 0 ? freeShippingThreshold : 1;
    const progressValue = Math.min(Math.max(total, 0), progressMax);
    const setProgress = (value) => {
      const safeValue = Math.min(Math.max(value, 0), progressMax);
      const percent = progressMax > 0 ? (safeValue / progressMax) * 100 : 0;

      shippingProgress.style.setProperty('--cart-shipping-progress', `${percent}%`);
      shippingProgress.setAttribute('aria-valuemax', String(progressMax));
      shippingProgress.setAttribute('aria-valuenow', String(safeValue));
    };

    setProgress(progressValue);

    if (count === 0) {
      shippingMessage.textContent = 'Add items to your bag to receive free shipping.';
      setProgress(0);
      return;
    }

    if (freeShippingThreshold <= 0) {
      shippingMessage.textContent = '';
      setProgress(0);
      return;
    }

    const remaining = Math.max(freeShippingThreshold - total, 0);

    shippingMessage.textContent = remaining > 0
      ? `You are ${formatMoney(remaining)} away from free shipping.`
      : 'Your bag qualifies for free shipping.';
  };

  const updateSubtotal = ({ displayTotal, normalTotal, exchangeTotal }) => {
    if (!subtotal) return;

    const currentSubtotal = subtotal.querySelector('[data-cart-subtotal-current]');
    if (currentSubtotal) {
      currentSubtotal.dataset.redoNormalSubtotal = formatMoney(normalTotal);
      currentSubtotal.dataset.redoExchangeSubtotal = formatMoney(exchangeTotal);
      currentSubtotal.textContent = formatMoney(displayTotal);
    } else {
      subtotal.textContent = formatMoney(displayTotal);
    }
  };

  const renderProperties = (properties) => {
    if (!properties || typeof properties !== 'object') return '';

    return Object.entries(properties)
      .filter(([key, value]) => value && !String(key).startsWith('_'))
      .map(([key, value]) => `<p class="cart-drawer-item__meta">${escapeHtml(key)}: ${escapeHtml(value)}</p>`)
      .join('');
  };

  const getItemOptions = (item) => {
    const options = Array.isArray(item?.options_with_values) ? item.options_with_values : [];
    const normalizedOptions = options
      .map((option) => ({
        name: formatOptionValue(option?.name),
        value: formatOptionValue(option?.value)
      }))
      .filter((option) => option.name && option.value && option.value !== 'Default Title');

    if (normalizedOptions.length) return normalizedOptions;

    if (item?.variant_title && item.variant_title !== 'Default Title') {
      return [{ name: 'Variant', value: formatOptionValue(item.variant_title) }];
    }

    return [];
  };

  const renderItemOptions = (item) => {
    const options = getItemOptions(item);

    if (!options.length) return '';

    return `
      <div class="cart-drawer-item__variants">
        ${options.map((option) => `
          <p class="cart-drawer-item__meta">
            <span>${escapeHtml(option.name)}:</span> ${escapeHtml(option.value)}
          </p>
        `).join('')}
      </div>
    `;
  };

  const getLineDiscounts = (item) => {
    const allocations = Array.isArray(item?.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : [];

    if (allocations.length) {
      return allocations
        .map((allocation) => ({
          amount: Number(allocation?.amount || 0),
          title: allocation?.discount_application?.title || allocation?.title || ''
        }))
        .filter((discount) => discount.title || discount.amount > 0);
    }

    const discounts = Array.isArray(item?.discounts) ? item.discounts : [];

    return discounts
      .map((discount) => ({
        amount: Number(discount?.amount || 0),
        title: discount?.title || ''
      }))
      .filter((discount) => discount.title || discount.amount > 0);
  };

  const renderLineDiscounts = (item, productSaleSavings = 0, shopifyDiscountSavings = 0) => {
    const discounts = getLineDiscounts(item);
    const saleMarkup = productSaleSavings > 0
      ? `
        <span class="cart-drawer-item__discounts cart-sale-surface">
          <span>Sale savings&nbsp;${formatMoney(productSaleSavings)}</span>
        </span>
      `
      : '';
    const discountMarkup = discounts.length || shopifyDiscountSavings > 0
      ? `
        <span class="cart-drawer-item__discounts cart-discount-surface">
          ${shopifyDiscountSavings > 0 && !discounts.length ? `<span>Discount savings&nbsp;${formatMoney(shopifyDiscountSavings)}</span>` : ''}
          ${discounts.map((discount) => `
            <span>
              ${escapeHtml(discount.title || 'Discount')}
              ${discount.amount > 0 ? ` -${formatMoney(discount.amount)}` : ''}
            </span>
          `).join('')}
        </span>
      `
      : '';

    return `${saleMarkup}${discountMarkup}`;
  };

  const renderCartItem = (item, index) => {
    const lineIndex = index + 1;
    const image = item.featured_image || {};
    const imageUrl = normalizeImageUrl(image.url || item.image);
    const imageAlt = escapeHtml(image.alt || item.product_title || 'Product image');
    const imageRatio = Number(image.aspect_ratio || 0);
    const imageHeight = imageRatio > 0 ? Math.round(180 / imageRatio) : 240;
    const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
      ? `<p class="cart-drawer-item__meta">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>`
      : '';
    const originalLinePrice = getItemOriginalLinePrice(item);
    const exchangeLinePrice = getItemExchangeLinePrice(item);
    const finalLinePrice = Number(item.final_line_price || 0);
    const exchangeActive = isRedoExchangeSession();
    const lineSavings = Math.max(originalLinePrice - finalLinePrice, 0);
    const productSaleSavings = Math.max(originalLinePrice - exchangeLinePrice, 0);
    const shopifyDiscountSavings = Math.max(exchangeLinePrice - finalLinePrice, 0);
    const hasDiscountedPrice = exchangeActive ? productSaleSavings > 0 : lineSavings > 0;
    const displayLinePrice = exchangeActive ? exchangeLinePrice : finalLinePrice;
    const normalDiscountPercent = originalLinePrice > finalLinePrice
      ? Math.round(((originalLinePrice - finalLinePrice) / originalLinePrice) * 100)
      : 0;
    const exchangeDiscountPercent = originalLinePrice > exchangeLinePrice
      ? Math.round(((originalLinePrice - exchangeLinePrice) / originalLinePrice) * 100)
      : 0;
    const originalUnitPrice = Math.round(originalLinePrice / Math.max(Number(item.quantity || 0), 1));
    const comparePrice = lineSavings > 0
      ? `<span class="visually-hidden">Original price</span><s class="${productSaleSavings > 0 ? 'cart-sale-surface' : 'cart-discount-surface'}">${formatMoney(originalLinePrice)}</s>`
      : '';

    cacheOriginalUnitPrice({
      key: item.key,
      variantId: getItemVariantId(item),
      unitPrice: originalUnitPrice
    });

    return `
      <article
        class="cart-drawer-item"
        data-cart-drawer-item
        data-line-key="${escapeHtml(item.key)}"
        data-line-index="${lineIndex}"
        data-product-id="${escapeHtml(item.product_id)}"
        data-product-handle="${escapeHtml(item.handle)}"
        data-product-title="${escapeHtml(item.product_title)}"
        data-variant-id="${escapeHtml(getItemVariantId(item))}"
        data-cart-original-unit-price="${originalUnitPrice}"
      >
        <a class="cart-drawer-item__media" href="${escapeHtml(item.url || '#')}" tabindex="-1" data-cart-product-link>
          ${imageUrl ? `
            <img
              data-product-image
              src="${escapeHtml(imageUrl)}"
              width="180"
              height="${imageHeight}"
              alt="${imageAlt}"
              loading="lazy"
            >
          ` : ''}
        </a>

        <div class="cart-drawer-item__content">
          <div class="cart-drawer-item__top">
            <h3><a href="${escapeHtml(item.url || '#')}" data-cart-product-link>${escapeHtml(item.product_title)}</a></h3>
            <button class="cart-drawer-item__remove" type="button" aria-label="Remove ${escapeHtml(item.product_title)}" data-cart-remove="${escapeHtml(item.key)}" data-cart-remove-line="${lineIndex}">
              ${getIcon('close')}
            </button>
          </div>

          ${renderItemOptions(item)}
          ${sellingPlan}
          ${renderProperties(item.properties)}

          <div class="cart-drawer-item__bottom">
            <div class="cart-quantity" data-cart-drawer-quantity data-line-key="${escapeHtml(item.key)}" data-line-index="${lineIndex}">
              <button class="cart-quantity__button" type="button" aria-label="Decrease quantity" data-cart-quantity-decrement>
                ${getIcon('minus')}
              </button>
              <input
                class="cart-quantity__input"
                type="number"
                value="${item.quantity}"
                min="0"
                inputmode="numeric"
                aria-label="Quantity for ${escapeHtml(item.product_title)}"
                data-cart-quantity-input
              >
              <button class="cart-quantity__button" type="button" aria-label="Increase quantity" data-cart-quantity-increment>
                ${getIcon('plus')}
              </button>
            </div>

            <p class="cart-drawer-item__price${hasDiscountedPrice ? ' cart-drawer-item__price--sale' : ''}${productSaleSavings > 0 ? ' cart-drawer-item__price--product-sale' : ''}">
              <span class="cart-price-line">
                ${comparePrice}
                <span class="visually-hidden">${hasDiscountedPrice ? 'Current promotional price' : 'Price'}</span>
                <span
                  class="cart-drawer-item__price-current"
                  data-redo-normal-line-price="${escapeHtml(formatMoney(finalLinePrice))}"
                  data-redo-exchange-line-price="${escapeHtml(formatMoney(exchangeLinePrice))}"
                >${formatMoney(displayLinePrice)}</span>
                ${normalDiscountPercent > 0 ? `<span class="price__discount cart-price-discount--normal" aria-label="${normalDiscountPercent} percent off">${normalDiscountPercent}% off</span>` : ''}
                ${exchangeDiscountPercent > 0 ? `<span class="price__discount cart-price-discount--exchange" aria-label="${exchangeDiscountPercent} percent off">${exchangeDiscountPercent}% off</span>` : ''}
              </span>
              ${renderLineDiscounts(item, productSaleSavings, shopifyDiscountSavings)}
            </p>
          </div>
        </div>
      </article>
    `;
  };

  const renderCart = (cart) => {
    const visibleEntries = getVisibleCartEntries(cart);
    const visibleCount = getVisibleCartCount(visibleEntries);
    const visibleTotal = getVisibleCartTotal(visibleEntries);
    const visibleOriginalTotal = getVisibleCartOriginalTotal(visibleEntries);
    const visibleExchangeTotal = getVisibleCartExchangeTotal(visibleEntries);
    const exchangeActive = isRedoExchangeSession();
    const displayTotal = exchangeActive ? visibleExchangeTotal : visibleTotal;
    const hasItems = visibleCount > 0;

    if (itemsContainer) {
      itemsContainer.innerHTML = visibleEntries
        .map((entry) => renderCartItem(entry.item, entry.index))
        .join('');
      itemsContainer.hidden = !hasItems;
    }

    if (emptyState) emptyState.hidden = hasItems;
    if (footer) footer.hidden = !hasItems;
    updateSubtotal({
      displayTotal,
      normalTotal: visibleTotal,
      exchangeTotal: visibleExchangeTotal
    });

    if (savingsRow && savingsTotal) {
      const normalSavings = Math.max(visibleOriginalTotal - visibleTotal, 0);
      const exchangeSaleSavings = Math.max(visibleOriginalTotal - visibleExchangeTotal, 0);
      const displaySavings = exchangeActive ? exchangeSaleSavings : normalSavings;

      savingsRow.dataset.redoNormalSavingsCents = String(normalSavings);
      savingsRow.dataset.redoExchangeSavingsCents = String(exchangeSaleSavings);
      savingsTotal.dataset.redoNormalSavings = formatMoney(normalSavings);
      savingsTotal.dataset.redoExchangeSavings = formatMoney(exchangeSaleSavings);
      savingsRow.hidden = displaySavings <= 0;
      savingsTotal.textContent = formatMoney(displaySavings);
    }

    updateHeaderCounts(visibleCount);
    updateShippingMessage({ count: visibleCount, total: displayTotal });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  const requestCart = async () => {
    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Could not load bag.');
    return response.json();
  };

  const refreshCart = async () => {
    drawer.classList.add('cart-drawer--loading');

    try {
      const cart = await requestCart();
      await hydrateOriginalUnitPrices(cart);
      renderCart(cart);
      return cart;
    } finally {
      drawer.classList.remove('cart-drawer--loading');
    }
  };

  const openDrawer = async ({ refresh = true } = {}) => {
    lastFocusedElement = document.activeElement;
    shell.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-is-open');
    document.body.classList.add('cart-drawer-is-open');
    isOpen = true;

    window.requestAnimationFrame(() => {
      shell.classList.add('cart-drawer-shell--open');
      const focusTarget = drawer.querySelector('.cart-drawer__close') || drawer;
      focusTarget.focus({ preventScroll: true });
    });

    if (refresh) {
      try {
        await refreshCart();
      } catch (error) {
        setStatus(error.message);
      }
    }
  };

  const closeDrawer = () => {
    if (!isOpen) return;

    shell.classList.remove('cart-drawer-shell--open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cart-drawer-is-open');
    document.body.classList.remove('cart-drawer-is-open');
    isOpen = false;

    const restoreFocus = () => {
      shell.hidden = true;

      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus({ preventScroll: true });
      }
    };

    if (prefersReducedMotion) {
      restoreFocus();
    } else {
      window.setTimeout(restoreFocus, 220);
    }
  };

  const escapeSelectorString = (value) => {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  };

  const getFormSubmitButtons = (form, submitter) => {
    const buttons = new Set();
    const addButton = (button) => {
      if (button instanceof HTMLButtonElement && button.type === 'submit') {
        buttons.add(button);
      }
    };

    addButton(submitter);
    form.querySelectorAll('button').forEach(addButton);

    if (form.id) {
      document.querySelectorAll(`button[form="${escapeSelectorString(form.id)}"]`).forEach(addButton);
    }

    return Array.from(buttons);
  };

  const setSubmitting = (form, submitter, isSubmitting) => {
    if (!form) return;

    const buttons = getFormSubmitButtons(form, submitter);

    if (isSubmitting) {
      form.setAttribute('aria-busy', 'true');
    } else {
      form.removeAttribute('aria-busy');
    }

    buttons.forEach((button) => {
      if (isSubmitting) {
        button.dataset.loadingWasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.classList.add('is-loading');
        return;
      }

      button.disabled = button.dataset.loadingWasDisabled === 'true';
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
      delete button.dataset.loadingWasDisabled;
    });
  };

  const getAddedQuantity = (formData) => {
    const quantity = Number.parseInt(formData.get('quantity') || '1', 10);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const getAddedCartLine = (cart, addedItem, formData) => {
    if (!cart?.items?.length) return null;

    if (addedItem?.key) {
      const keyedItem = cart.items.find((item) => item.key === addedItem.key);
      if (keyedItem) return { item: keyedItem, line: cart.items.indexOf(keyedItem) + 1 };
    }

    const variantId = Number.parseInt(addedItem?.variant_id || formData.get('id'), 10);
    if (!Number.isFinite(variantId)) return null;

    const matchingIndex = cart.items.findIndex((item) => item.variant_id === variantId);
    if (matchingIndex < 0) return null;

    return {
      item: cart.items[matchingIndex],
      line: matchingIndex + 1
    };
  };

  const shouldShowAddToast = (form) => {
    return Boolean(form.closest('.product-card') || form.matches('[data-product-form]'));
  };

  const getToastMessage = (form, item) => {
    return item?.product_title
      || form.closest('.product-card')?.querySelector('.product-card__title')?.textContent?.trim()
      || form.querySelector('h1')?.textContent?.trim()
      || '';
  };

  const addFormToCart = async (form, submitter) => {
    const formData = new FormData(form);
    const useAddToast = shouldShowAddToast(form);

    setSubmitting(form, submitter, true);
    setStatus('');

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'This item could not be added.');
      }

      const addedItem = await response.json();
      const cart = await refreshCart();
      const addedLine = getAddedCartLine(cart, addedItem, formData);

      const quickAdd = form.closest('details.product-card__quick-add');
      if (quickAdd) quickAdd.open = false;

      if (useAddToast) {
        const toastItem = addedLine?.item || addedItem;

        await showToast({
          heading: 'Added to bag',
          message: getToastMessage(form, toastItem),
          item: toastItem,
          undo: addedLine ? {
            key: addedLine.item.key,
            line: addedLine.line,
            quantity: getAddedQuantity(formData)
          } : null
        });
        return;
      }

      await openDrawer({ refresh: false });
      setStatus('Added to bag.');
    } catch (error) {
      if (useAddToast) {
        await showToast({
          heading: 'Could not add item',
          message: error.message,
          type: 'error'
        });
      } else {
        setStatus(error.message);
        await openDrawer({ refresh: true });
      }
    } finally {
      setSubmitting(form, submitter, false);
    }
  };

  const addVariantToCartWithToast = async ({
    id,
    quantity = 1,
    message = ''
  } = {}) => {
    const variantId = Number.parseInt(toStringId(id), 10);
    const addQuantity = Math.max(Number.parseInt(quantity, 10) || 1, 1);

    if (!Number.isFinite(variantId)) throw new Error('This item could not be added.');

    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: addQuantity }] })
    });

    if (!response.ok) throw await getCartError(response, 'This item could not be added.');

    const addedResponse = await response.json();
    const addedItem = Array.isArray(addedResponse.items) ? addedResponse.items[0] : addedResponse;
    const cart = await refreshCart();
    const formData = new FormData();

    formData.set('id', String(variantId));
    formData.set('quantity', String(addQuantity));

    const addedLine = getAddedCartLine(cart, addedItem, formData);
    const toastItem = addedLine?.item || addedItem;

    await showToast({
      heading: 'Added to bag',
      message: message || toastItem?.product_title || '',
      item: toastItem,
      undo: addedLine ? {
        key: addedLine.item.key,
        line: addedLine.line,
        quantity: addQuantity
      } : null
    });

    return { cart, item: toastItem, line: addedLine };
  };

  const removeCartLine = async ({ key, line }) => {
    const errors = [];
    let cart = null;

    if (key) {
      try {
        cart = await postCartJson(updateUrl, { updates: { [key]: 0 } }, 'Could not remove item.');
        if (!cartHasKey(cart, key)) return cart;
      } catch (error) {
        errors.push(error);
      }
    }

    if (line) {
      try {
        cart = await postCartJson(changeUrl, { line, quantity: 0 }, 'Could not remove item.');
        if (!key || !cartHasKey(cart, key)) return cart;
      } catch (error) {
        errors.push(error);
      }
    }

    if (cart) return cart;
    throw errors[0] || new Error('Could not remove item.');
  };

  const changeDrawerQuantity = async ({ key, line }, quantity) => {
    drawer.classList.add('cart-drawer--loading');
    setStatus('');

    try {
      let cart = null;

      try {
        cart = await postCartJson(changeUrl, { id: key, quantity }, 'Could not update bag.');
      } catch (error) {
        if (!line) throw error;
        cart = await postCartJson(changeUrl, { line, quantity }, 'Could not update bag.');
      }

      if (quantity === 0 && cartHasKey(cart, key)) {
        cart = await removeCartLine({ key, line });
      }

      renderCart(cart);
    } catch (error) {
      setStatus(error.message);
      await refreshCart();
    } finally {
      drawer.classList.remove('cart-drawer--loading');
    }
  };

  const undoQuickAdd = async ({ key, quantity }) => {
    if (!key) return;

    window.clearTimeout(toastTimer);
    const undoButton = toastRegion?.querySelector('[data-cart-toast-undo]');
    if (undoButton) {
      undoButton.disabled = true;
      undoButton.setAttribute('aria-busy', 'true');
    }

    try {
      const cart = await requestCart();
      const lineIndex = cart.items.findIndex((item) => item.key === key);

      if (lineIndex < 0) {
        hideToast();
        return;
      }

      const currentItem = cart.items[lineIndex];
      const nextQuantity = Math.max(Number(currentItem.quantity || 0) - quantity, 0);
      let nextCart = null;

      if (nextQuantity === 0) {
        nextCart = await removeCartLine({ key, line: lineIndex + 1 });
      } else {
        try {
          nextCart = await postCartJson(changeUrl, { id: key, quantity: nextQuantity }, 'Could not undo add.');
        } catch (error) {
          nextCart = await postCartJson(changeUrl, { line: lineIndex + 1, quantity: nextQuantity }, 'Could not undo add.');
        }
      }

      renderCart(nextCart);
      hideToast();
    } catch (error) {
      showToast({
        heading: 'Could not undo add',
        message: error.message,
        type: 'error'
      });
    }
  };

  const getQuantityValue = (input) => {
    const value = Number.parseInt(input.value, 10);
    return Number.isFinite(value) ? value : 0;
  };

  const syncPageQuantity = (button) => {
    const control = button.closest('[data-cart-page-quantity]');
    const input = control?.querySelector('[data-cart-page-quantity-input]');

    if (!input) return;

    const nextValue = button.matches('[data-cart-quantity-decrement]')
      ? Math.max(getQuantityValue(input) - 1, 0)
      : getQuantityValue(input) + 1;

    input.value = String(nextValue);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  document.addEventListener('click', (event) => {
    const openTrigger = event.target.closest('[data-cart-drawer-open]');

    if (
      openTrigger &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const toastCloseButton = event.target.closest('[data-cart-toast-close]');
    if (toastCloseButton) {
      event.preventDefault();
      hideToast();
      return;
    }

    const toastOpenBagButton = event.target.closest('[data-cart-toast-open-bag]');
    if (toastOpenBagButton) {
      event.preventDefault();
      hideToast();
      openDrawer({ refresh: false });
      return;
    }

    const toastUndoButton = event.target.closest('[data-cart-toast-undo]');
    if (toastUndoButton) {
      event.preventDefault();
      undoQuickAdd({
        key: toastUndoButton.dataset.cartToastUndoKey,
        quantity: Math.max(Number.parseInt(toastUndoButton.dataset.cartToastUndoQuantity || '1', 10), 1)
      });
      return;
    }

    const pageQuantityButton = event.target.closest('[data-cart-page-quantity] [data-cart-quantity-decrement], [data-cart-page-quantity] [data-cart-quantity-increment]');
    if (pageQuantityButton) {
      event.preventDefault();
      syncPageQuantity(pageQuantityButton);
      return;
    }

    const drawerQuantityButton = event.target.closest('[data-cart-drawer-quantity] [data-cart-quantity-decrement], [data-cart-drawer-quantity] [data-cart-quantity-increment]');
    if (drawerQuantityButton) {
      event.preventDefault();

      const control = drawerQuantityButton.closest('[data-cart-drawer-quantity]');
      const input = control?.querySelector('[data-cart-quantity-input]');
      const key = control?.dataset.lineKey;
      const line = getLineNumber(control?.dataset.lineIndex);

      if (!input || !key) return;

      const nextValue = drawerQuantityButton.matches('[data-cart-quantity-decrement]')
        ? Math.max(getQuantityValue(input) - 1, 0)
        : getQuantityValue(input) + 1;

      input.value = String(nextValue);
      changeDrawerQuantity({ key, line }, nextValue);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      changeDrawerQuantity({
        key: removeButton.dataset.cartRemove,
        line: getLineNumber(removeButton.dataset.cartRemoveLine)
      }, 0);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-drawer-quantity] [data-cart-quantity-input]');
    const control = input?.closest('[data-cart-drawer-quantity]');
    const key = control?.dataset.lineKey;
    const line = getLineNumber(control?.dataset.lineIndex);

    if (!input || !key) return;

    changeDrawerQuantity({ key, line }, Math.max(getQuantityValue(input), 0));
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-cart-add-form]');

    if (!form) return;

    event.preventDefault();
    addFormToCart(form, event.submitter || form.querySelector('[type="submit"]'));
  });

  document.addEventListener('cart:add-item', async (event) => {
    const detail = event.detail || {};
    detail.handled = true;

    try {
      const result = await addVariantToCartWithToast({
        id: detail.id,
        quantity: detail.quantity,
        message: detail.message
      });

      if (typeof detail.onSuccess === 'function') detail.onSuccess(result);
    } catch (error) {
      await showToast({
        heading: 'Could not add item',
        message: error.message,
        type: 'error'
      });

      if (typeof detail.onError === 'function') detail.onError(error);
    }
  });

  document.addEventListener('cart:replace', (event) => {
    if (!event.detail?.cart) return;

    renderCart(event.detail.cart);
  });

  const resetRestoredCartState = async () => {
    drawer.classList.remove('cart-drawer--loading');
    document.documentElement.classList.remove('cart-state-is-pending');

    drawer.querySelectorAll('form[aria-busy="true"]').forEach((form) => {
      form.removeAttribute('aria-busy');
    });

    drawer.querySelectorAll('[aria-busy="true"], .is-loading').forEach((control) => {
      control.removeAttribute('aria-busy');
      control.classList.remove('is-loading');

      if (control instanceof HTMLButtonElement && control.dataset.loadingWasDisabled !== 'true') {
        control.disabled = false;
      }

      delete control.dataset.loadingWasDisabled;
    });

    try {
      await refreshCart();
      setStatus('');
    } catch (error) {
      drawer.classList.remove('cart-drawer--loading');
      setStatus(error.message || 'Could not refresh your bag.');
    }
  };

  window.addEventListener('pageshow', (event) => {
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    const restoredFromHistory = event.persisted || navigation?.type === 'back_forward';

    if (!restoredFromHistory) return;
    resetRestoredCartState();
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      drawer.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
